import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarModule } from '@angular/material/snack-bar';
import { CurrentUser } from '../../../common/models/user';
import { StorageService } from '../../../common/services/storage.service';
import { SearchService } from '../../../common/services/search.service';
import { CustomizerSettingsService } from '../../../customizer-settings/customizer-settings.service';
import { ApplicationService } from '../../../common/services/application.service';
import { ApplicationFormType, ApplicationSummary, GetApplicationsFilter } from '../../../common/models/application';
import { GenericDialogComponent } from '../../../common/components/dialog/dialog';
import { Guid } from 'guid-typescript';
import { Subject, takeUntil } from 'rxjs';
import { FilterService } from '../../../common/components/filter/services/filter.service';
import { ApplicationsFilterBuilder } from '../../../common/components/filter/builders/applications-filter-builder';
import { ApplicationStatus } from '../../../common/enums/application-status';

@Component({
    selector: 'app-applications',
    templateUrl: './applications.component.html',
    styleUrl: './applications.component.scss',
    imports: [MatCardModule, MatMenuModule, MatButtonModule, MatPaginatorModule, MatTooltipModule, MatIconModule, MatDialogModule, MatSnackBarModule, CommonModule]
})
export class ApplicationsComponent implements OnInit {
    private destroy$ = new Subject<void>();

    applications: ApplicationSummary[] = [];
    filteredApplications: ApplicationSummary[] = [];
    public ApplicationStatus = ApplicationStatus;
    public ApplicationFormType = ApplicationFormType;
    user: CurrentUser | null = null;
    isToggled = false;

    private readonly snackbarConfig: MatSnackBarConfig = {
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top'
    };

    constructor(
      private router: Router,
      private filterBuilder: ApplicationsFilterBuilder,
      private applicationService: ApplicationService,
      private storageService: StorageService,
      private searchService: SearchService,
      private filterService: FilterService,
      public themeService: CustomizerSettingsService,
      private dialog: MatDialog,
      private snackBar: MatSnackBar
    ) {}
  
    ngOnInit() {
      this.themeService.isToggled$.pipe(takeUntil(this.destroy$)).subscribe(isToggled => {
        this.isToggled = isToggled;
      });
      this.searchService.currentSearchTerm$.pipe(takeUntil(this.destroy$)).subscribe(term => {
        this.applySearch(term);
      });
      this.filterService.filterChanges$.pipe(takeUntil(this.destroy$)).subscribe((filters) => {
          this.filterService.setFiltersInScope(filters);
          this.fetchApplications();
      });
      this.storageService.user$.pipe(takeUntil(this.destroy$)).subscribe(user => {
        this.user = user;
        this.initializeFilters();
      });
   
    }

    ngOnDestroy() {
      this.destroy$.next();
      this.destroy$.complete();
    }

    initializeFilters(): void {
      const startYear = 2025;
      const currentYear = new Date().getFullYear();
    
      const config = this.filterBuilder
        .reset()
        .years(startYear)
        .build();

      this.filterService.setFilterConfig(config);
      this.filterService.restoreFiltersInScope({ year: currentYear });        
    }
  
    fetchApplications(): void {
        const savedFilterValues = this.filterService.getFiltersInScope();
        
        const associationId = this.user?.associationId ?? '';
        const municipalityId = this.user?.municipalityId ?? '';

        var filter = new GetApplicationsFilter(
          municipalityId,
          associationId, 
          undefined, 
          savedFilterValues?.year,
          undefined
        );

        this.applicationService.getApplications(filter).pipe(takeUntil(this.destroy$)).subscribe({
          next: (data) => {
            this.applications = data.map(a => ApplicationSummary.fromJson(a));
            this.filteredApplications = [...this.applications];
          },
          error: (error) => {
            console.error('Error fetching applications', error);
          }
        });
    }

    applySearch(searchTerm: string): void {
      searchTerm = searchTerm.trim().toLowerCase();
      if (searchTerm) {
        this.filteredApplications = this.applications.filter((application: ApplicationSummary) => {
          return (
            application.grantName.toLowerCase().includes(searchTerm)  ||
            application.organizationName.toLowerCase().includes(searchTerm) ||
            application.programProjectName.toLowerCase().includes(searchTerm)
          );
        });
      } else {
        this.filteredApplications = [...this.applications];
      }
    }


      confirmSubmit(application: ApplicationSummary): void {
        const dialogRef = this.dialog.open(GenericDialogComponent, {
          width: '50%',
          data: {
            title: 'Potvrdite predaju',
            message: `Jeste li sigurni da želite predati prijavu "${application.programProjectName ?? ''}"?`,

              confirmButtonText: 'Da',
            cancelButtonText: 'Ne',
          },
        });
    
        dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
          if (result === true) {
            this.submitApplication(application);
          }
        });
      }

      private submitApplication(application: ApplicationSummary): void {
        this.applicationService.submit(application.id).pipe(takeUntil(this.destroy$)).subscribe({
          next: () => {
            this.fetchApplications();
            this.snackBar.open('Prijava je predana', 'Zatvori', this.snackbarConfig);            
          },
          error: (error) => {
            console.error('Greška prilikom predavanja prijave', error);
          }
        });
      }

      confirmResubmit(application: ApplicationSummary): void {
        const dialogRef = this.dialog.open(GenericDialogComponent, {
          width: '50%',
          data: {
            title: 'Potvrdite ponovnu predaju',
            message: `Jeste li sigurni da želite ponovno predati prijavu "${application.programProjectName ?? ''}"?`,
            confirmButtonText: 'Da',
            cancelButtonText: 'Ne',
          },
        });
    
        dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
          if (result === true) {
            this.resubmitApplication(application);
          }
        });
      }

      private resubmitApplication(application: ApplicationSummary): void {
        this.applicationService.resubmit(application.id).pipe(takeUntil(this.destroy$)).subscribe({
          next: () => {
            this.fetchApplications();
            this.snackBar.open('Prijava je predana', 'Zatvori', this.snackbarConfig);
          },
          error: (error) => {
            console.error('Greška prilikom predavanja prijave', error);
          }
        });
    }

    confirmDelete(application: ApplicationSummary): void {
        const dialogRef = this.dialog.open(GenericDialogComponent, {
            width: '50%',
            data: {
                title: 'Potvrdite brisanje',
                message: `Jeste li sigurni da želite obrisati prijavu na natječaj "${application.grantName}"?`,
                confirmButtonText: 'Da',
                cancelButtonText: 'Ne',
            },
        });

        dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
            if (result === true) {
                this.deleteApplication(application.id);
            }
        });
    }
    
      deleteApplication(applicationId: Guid): void {
        this.applicationService.deleteApplication(applicationId).pipe(takeUntil(this.destroy$)).subscribe({
          next: () => {
            this.fetchApplications();
            this.snackBar.open('Prijava je obrisana', 'Zatvori', this.snackbarConfig);
          },
          error: (error) => {
            console.error('Error deleting application', error);
          }
        });
      }

      downloadForms(application: ApplicationSummary, formTypes: ApplicationFormType[]): void {
            this.applicationService.downloadForms(application.id, formTypes).pipe(takeUntil(this.destroy$)).subscribe({
                next: (blob) => {
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'prijavna-dokumentacija.zip';
                    a.click();
                    window.URL.revokeObjectURL(url);
                },
                error: (err) => {
                    console.error('Error downloading application forms', err);
                }
            });
         }
  
    goToViewApplication(application: ApplicationSummary): void {
      this.router.navigate(['association', 'applications', application.id.toString(), 'view']);
    }
    

    goToEditApplication(applicaion: ApplicationSummary): void {
      this.router.navigate(['association', 'applications', applicaion.id.toString(), 'edit']);
    }
  }
